import { supabase } from '@/lib/supabaseClient';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_WEB_PUSH_PUBLIC_KEY;

const SERVICE_WORKER_SCRIPT_URL = '/sw.js';
const SERVICE_WORKER_READY_TIMEOUT_MS = 10_000;

async function waitForActiveServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    throw new Error('El entorno actual no permite gestionar notificaciones push.');
  }

  if (!('serviceWorker' in navigator)) {
    throw new Error('Este dispositivo no puede registrar un Service Worker para notificaciones push.');
  }

  const ensureReady = async (): Promise<ServiceWorkerRegistration> => {
    let timeoutId: number | undefined;

    try {
      const readyRegistration = await Promise.race<ServiceWorkerRegistration>([
        navigator.serviceWorker.ready,
        new Promise<ServiceWorkerRegistration>((_, reject) => {
          timeoutId = window.setTimeout(async () => {
            const registration = await navigator.serviceWorker.getRegistration();

            if (!registration) {
              reject(
                new Error(
                  window.isSecureContext
                    ? 'No se pudo inicializar el Service Worker. Recarga la aplicación e inténtalo de nuevo.'
                    : 'Activa las notificaciones desde una conexión segura (HTTPS o localhost).',
                ),
              );
              return;
            }

            if (!registration.active) {
              reject(new Error('El Service Worker tardó demasiado en iniciar. Recarga la aplicación e inténtalo de nuevo.'));
              return;
            }

            reject(new Error('No se pudo preparar el Service Worker para gestionar notificaciones push.'));
          }, SERVICE_WORKER_READY_TIMEOUT_MS);
        }),
      ]);

      return readyRegistration;
    } finally {
      if (typeof timeoutId === 'number') {
        window.clearTimeout(timeoutId);
      }
    }
  };

  const existingRegistration = await navigator.serviceWorker.getRegistration();

  if (existingRegistration) {
    if (existingRegistration.active) {
      return existingRegistration;
    }

    return ensureReady();
  }

  if (!window.isSecureContext) {
    throw new Error('Para activar las notificaciones push accede mediante HTTPS o localhost.');
  }

  try {
    const registration = await navigator.serviceWorker.register(SERVICE_WORKER_SCRIPT_URL);

    if (registration.active) {
      return registration;
    }

    return ensureReady();
  } catch (error) {
    console.error('No se pudo registrar el Service Worker para notificaciones push:', error);
    throw new Error('No se pudo registrar el Service Worker para las notificaciones push.');
  }
}

async function persistPushSubscription(
  userId: string,
  subscription: PushSubscriptionJSON,
): Promise<void> {
  const timestamp = new Date().toISOString();

  const { data, error } = await supabase
    .from('user_push_notifications')
    .upsert(
      {
        user_id: userId,
        subscription,
        updated_at: timestamp,
      },
      { onConflict: 'user_id' }
    )
    .select('user_id')
    .maybeSingle();

  if (error) {
    throw new Error(
      error.message || 'No se pudo registrar el dispositivo para notificaciones push.',
    );
  }

  if (!data) {
    throw new Error('No se pudo registrar el dispositivo para notificaciones push.');
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = globalThis.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

export function isPushNotificationSupported(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const hasNotificationApi = 'Notification' in window;
  const hasServiceWorker = 'serviceWorker' in navigator;

  if (!hasNotificationApi || !hasServiceWorker) {
    return false;
  }

  const hasPushManagerGlobal = 'PushManager' in window;
  const registrationConstructor = (window as typeof window & {
    ServiceWorkerRegistration?: { prototype?: unknown };
  }).ServiceWorkerRegistration;
  const registrationPrototype = registrationConstructor?.prototype;
  const hasPushManagerPrototype = Boolean(
    registrationPrototype
    && typeof registrationPrototype === 'object'
    && registrationPrototype !== null
    && (
      'pushManager' in registrationPrototype
      || Object.getOwnPropertyDescriptor(registrationPrototype, 'pushManager')
    )
  );

  if (hasPushManagerGlobal || hasPushManagerPrototype) {
    return true;
  }

  const userAgent = navigator.userAgent || '';
  const isAppleDevice = /iP(ad|hone|od)/.test(userAgent);
  const isWebKit = /WebKit/.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(userAgent);
  const match = userAgent.match(/OS\s(\d+)[_.](\d+)_?(\d+)?/);
  const iosMajor = match ? Number.parseInt(match[1], 10) : undefined;
  const iosMinor = match ? Number.parseInt(match[2], 10) : undefined;
  const supportsIosPush = Boolean(
    isAppleDevice
    && isWebKit
    && iosMajor !== undefined
    && (iosMajor > 16 || (iosMajor === 16 && (iosMinor ?? 0) >= 4))
    && (
      (typeof window.matchMedia === 'function'
        && window.matchMedia('(display-mode: standalone)').matches)
      || (navigator as Navigator & { standalone?: boolean }).standalone === true
    )
  );

  if (supportsIosPush) {
    return true;
  }

  return false;
}

export async function ensurePushSubscriptionForUser(userId: string): Promise<PushSubscriptionJSON> {
  if (!isPushNotificationSupported()) {
    throw new Error('Este dispositivo no soporta notificaciones push.');
  }

  if (!VAPID_PUBLIC_KEY) {
    throw new Error('La configuración de notificaciones push no está disponible.');
  }

  if (Notification.permission === 'denied') {
    throw new Error('Has bloqueado las notificaciones. Actívalas desde los ajustes del dispositivo.');
  }

  const permission = Notification.permission === 'granted'
    ? 'granted'
    : await Notification.requestPermission();

  if (permission !== 'granted') {
    throw new Error('Necesitamos tu permiso para enviarte notificaciones.');
  }

  const registration = await waitForActiveServiceWorker();
  const pushManager = registration.pushManager;

  if (!pushManager) {
    throw new Error('Este dispositivo no permite gestionar suscripciones push.');
  }

  const existingSubscription = await pushManager.getSubscription();
  const subscriptionWasCreated = !existingSubscription;

  const subscription = existingSubscription
    ?? await pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });

  const payload = subscription.toJSON();

  await persistPushSubscription(userId, payload);

  const { error: userUpdateError } = await supabase
    .from('users')
    .update({ noti_push: true })
    .eq('id', userId);

  if (userUpdateError) {
    if (subscriptionWasCreated) {
      try {
        await subscription.unsubscribe();
      } catch (unsubscribeError) {
        console.warn('No se pudo revertir la suscripción push tras un error:', unsubscribeError);
      }
    }

    try {
      await supabase
        .from('user_push_notifications')
        .delete()
        .eq('user_id', userId);
    } catch (cleanupError) {
      console.warn('No se pudo revertir el registro en user_push_notifications:', cleanupError);
    }

    throw new Error(
      userUpdateError.message
      || 'No se pudo actualizar la preferencia de notificaciones push del usuario.'
    );
  }

  return payload;
}

export async function syncPushSubscriptionForUser(userId: string): Promise<void> {
  if (!userId) {
    return;
  }

  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return;
  }

  if (!isPushNotificationSupported()) {
    return;
  }

  if (Notification.permission !== 'granted') {
    return;
  }

  try {
    const registration = await waitForActiveServiceWorker();
    const pushManager = registration.pushManager;

    if (!pushManager) {
      return;
    }

    const subscription = await pushManager.getSubscription();

    if (!subscription) {
      return;
    }

    await persistPushSubscription(userId, subscription.toJSON());
  } catch (error) {
    const message = error instanceof Error
      ? error.message
      : 'No se pudo sincronizar la suscripción push.';
    throw new Error(message);
  }
}

export async function removePushSubscriptionForUser(userId: string): Promise<void> {
  if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
    try {
      const registration = await waitForActiveServiceWorker();
      const subscription = await registration.pushManager?.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
    } catch (error) {
      console.warn('No se pudo cancelar la suscripción push del Service Worker:', error);
    }
  }

  const { error } = await supabase
    .from('user_push_notifications')
    .delete()
    .eq('user_id', userId);

  if (error) {
    throw new Error(error.message || 'No se pudieron desactivar las notificaciones push.');
  }

  const { error: userUpdateError } = await supabase
    .from('users')
    .update({ noti_push: false })
    .eq('id', userId);

  if (userUpdateError) {
    throw new Error(
      userUpdateError.message
      || 'No se pudo actualizar la preferencia de notificaciones push del usuario.'
    );
  }
}
